import { CompanyDetailsApiResponse } from '../service/models/companyDetails'

/**
 * Dropdown option for brand and company selection.
 */
export interface DropdownOption {
	label: string
	value: string
	disabled?: boolean
}

/**
 * Builds a brand dropdown list from available companies.
 *
 * Uses brandNm from API response. Requires brandNm to be present in company data.
 * If only one brand is available, returns that brand as a disabled default.
 * If multiple brands, includes "Select one" placeholder.
 *
 * @param companies - Array of company details from API
 * @returns Array of dropdown options for brand selection
 */
export function buildBrandDropdownList(
	companies: CompanyDetailsApiResponse[]
): DropdownOption[] {
	if (!companies || companies.length === 0) {
		return [{ label: 'Select one', value: 'selectone', disabled: true }]
	}

	// Create a map of unique brands with their names from API
	const brandMap = new Map<string, string>()
	for (const company of companies) {
		if (company.brandCd && company.brandNm && !brandMap.has(company.brandCd)) {
			brandMap.set(company.brandCd, company.brandNm)
		}
	}

	const brandOptions = Array.from(brandMap.entries()).map(([code, name]) => ({
		label: name,
		value: code,
	}))

	if (brandOptions.length === 0) {
		return [{ label: 'Select one', value: 'selectone', disabled: true }]
	}

	if (brandOptions.length === 1) {
		return [
			{
				...brandOptions[0],
				disabled: true,
			},
		]
	}

	// Multiple brands - show select one + all options
	return [
		{ label: 'Select one', value: 'selectone', disabled: true },
		...brandOptions,
	]
}

/**
 * Builds a company dropdown list, optionally filtered by brand.
 *
 * Company labels use the format: "{referenceId|companyId} - {companyName}"
 * When referenceId is not available, falls back to companyId.
 *
 * @param companies - Array of company details
 * @param selectedBrand - Optional brand filter (omit or use 'selectone' for all)
 * @returns Array of dropdown options for company selection
 */
export function buildCompanyDropdownList(
	companies: CompanyDetailsApiResponse[],
	selectedBrand?: string
): DropdownOption[] {
	// Filter by brand if one is selected
	const filteredCompanies =
		!selectedBrand || selectedBrand === 'selectone'
			? companies
			: companies.filter(c => c.brandCd === selectedBrand)

	return filteredCompanies.map(company => {
		const label = company.referenceId
			? `${company.referenceId} - ${company.companyNm}`
			: `${company.companyNm}`
		return {
			label,
			value: company.companyId,
		}
	})
}
